import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from './auth.store';

// In dev, vite proxies /api → backend. In prod, panel and API are on different subdomains;
// VITE_API_BASE is set at build time via .env.production (or fall back to the api host).
const API_BASE =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.PROD ? 'https://api.replai.axccelerate.com' : '');

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
});

// Inject Authorization header from the store.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// On 401, try refresh-token rotation once. If that fails, kick the user back to /login.
let refreshing: Promise<string | null> | null = null;

const performRefresh = async (): Promise<string | null> => {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) {
    clear();
    return null;
  }
  try {
    const { data } = await axios.post(
      `${API_BASE}/api/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    setTokens(data.accessToken, data.refreshToken);
    return data.accessToken as string;
  } catch {
    clear();
    return null;
  }
};

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      refreshing ??= performRefresh().finally(() => {
        refreshing = null;
      });
      const newToken = await refreshing;
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return api.request(original);
      }
    }
    return Promise.reject(error);
  },
);

// Extracts the human-readable error message from an axios error (or any thrown value).
export const errorMessage = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const apiErr = err.response?.data as { error?: { message?: string } } | undefined;
    return apiErr?.error?.message ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
};
